import { useEffect, useState, Fragment, useRef } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Calendar, DollarSign, User, AlertCircle, CheckCircle, XCircle, Edit2, Trash2, Printer, Receipt, RotateCw, Download, Share2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import SubscriptionCertificate from "@/components/SubscriptionCertificate";
import { printReceipt, downloadReceiptPDF, shareReceiptPDF, numberToWords } from "@/utils/printUtils";
import ReceiptPrint from "@/components/ReceiptPrint";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/utils/api";

const Subscriptions = () => {
  const { formatDate, formatCurrency, settings } = useSettings();
  const certificateRef = useRef();
  const [subscriptions, setSubscriptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [printSubscription, setPrintSubscription] = useState(null);
  const [openHistory, setOpenHistory] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [isReceiptEditOpen, setIsReceiptEditOpen] = useState(false);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [receiptEditForm, setReceiptEditForm] = useState({ amount: '', payment_method: 'cash', description: '' });
  const [showCertificatePrintDialog, setShowCertificatePrintDialog] = useState(false);
  const [formData, setFormData] = useState({
    member_id: "",
    plan_id: "",
    start_date: new Date().toISOString().split('T')[0],
  });
  const [editFormData, setEditFormData] = useState({
    plan_id: "",
    start_date: "",
    payment_status: ""
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('active');
  const [memberFilter, setMemberFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  
  // Payment Dialog State
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    receipt_type: "subscription_fee",
    member_id: "",
    membership_id: "",
    amount: "",
    payment_method: "cash",
    description: "",
    date: new Date().toISOString().split('T')[0]
  });
  
  // Receipt Printing State
  const receiptRef = useRef();
  const [lastCreatedReceipt, setLastCreatedReceipt] = useState(null);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Get company info
    const user = JSON.parse(localStorage.getItem('user'));
    if (user?.company) {
      setCompany(user.company);
    }

    const handler = () => fetchData();
    window.addEventListener('ledgerUpdated', handler);
    return () => window.removeEventListener('ledgerUpdated', handler);
  }, []);

  const fetchData = async () => {
    try {
      const [subsRes, membersRes, plansRes] = await Promise.all([
        api.get('/memberships'),
        api.get('/members'),
        api.get('/membership-plans'),
      ]);
      setSubscriptions(subsRes.data);
      setMembers(membersRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.member_id || !formData.plan_id) {
      toast.error("Please select both member and plan");
      return;
    }

    try {
      await api.post('/memberships', formData);
      toast.success("Subscription created successfully");
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create subscription");
    }
  };

  const resetForm = () => {
    setFormData({
      member_id: "",
      plan_id: "",
      start_date: new Date().toISOString().split('T')[0],
    });
  };

  const handleEdit = (subscription) => {
    setEditingSubscription(subscription);
    setEditFormData({
      plan_id: subscription.plan_id,
      start_date: subscription.start_date.split('T')[0],
      payment_status: subscription.payment_status
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/memberships/${editingSubscription.id}`, editFormData);
      toast.success("Subscription updated successfully");
      fetchData();
      setIsEditDialogOpen(false);
      setEditingSubscription(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update subscription");
    }
  };

  const handleDelete = async (subscriptionId) => {
    if (window.confirm("Are you sure you want to delete this subscription? This action cannot be undone.")) {
      try {
        await api.delete(`/memberships/${subscriptionId}`);
        toast.success("Subscription deleted successfully");
        fetchData();
      } catch (error) {
        toast.error("Failed to delete subscription");
      }
    }
  };

  const toggleHistory = async (sub) => {
    const isOpen = !!openHistory[sub.id];
    if (!isOpen && !historyData[sub.id]) {
      try {
        const res = await api.get(`/memberships/${sub.id}/receipts`);
        setHistoryData(prev => ({ ...prev, [sub.id]: res.data }));
      } catch {
        toast.error("Failed to load payment history");
      }
    }
    setOpenHistory(prev => ({ ...prev, [sub.id]: !isOpen }));
  };

  const openReceiptEdit = (receipt) => {
    setEditingReceipt(receipt);
    setReceiptEditForm({
      amount: String(receipt.amount || ''),
      payment_method: receipt.payment_method || 'cash',
      description: receipt.description || ''
    });
    setIsReceiptEditOpen(true);
  };

  const submitReceiptEdit = async (e) => {
    e.preventDefault();
    if (!editingReceipt) return;
    try {
      const payload = {
        amount: parseFloat(receiptEditForm.amount),
        payment_method: receiptEditForm.payment_method,
        description: receiptEditForm.description
      };
      await api.patch(`/receipts/${editingReceipt.id}`, payload);
      toast.success('Receipt updated');
      setIsReceiptEditOpen(false);
      setEditingReceipt(null);
      const subId = editingReceipt.membership_id;
      try {
        const res = await api.get(`/memberships/${subId}/receipts`);
        setHistoryData(prev => ({ ...prev, [subId]: res.data }));
      } catch {}
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update receipt');
    }
  };

  const deleteReceipt = async (receipt) => {
    if (!window.confirm('Delete this receipt?')) return;
    try {
      await api.delete(`/receipts/${receipt.id}`);
      toast.success('Receipt deleted');
      const subId = receipt.membership_id;
      try {
        const res = await api.get(`/memberships/${subId}/receipts`);
        setHistoryData(prev => ({ ...prev, [subId]: res.data }));
      } catch {}
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete receipt');
    }
  };

  const handlePrintCertificate = (subscription) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const member = members.find(m => m.id === subscription.member_id);
    const plan = plans.find(p => p.id === subscription.plan_id);
    
    setPrintSubscription({
      subscription,
      member,
      plan,
      company: user?.company
    });
    setShowCertificatePrintDialog(true);
  };

  const handleCertificatePrint = (format) => {
    if (certificateRef.current) {
      printReceipt(certificateRef.current, format);
    }
  };

  const handleCertificateDownloadPDF = async (format) => {
    if (certificateRef.current && printSubscription) {
      const filename = `membership-certificate-${printSubscription.subscription?.id?.slice(0,8)}.pdf`;
      const success = await downloadReceiptPDF(certificateRef.current, filename, format);
      if (success) {
        toast.success('Certificate PDF downloaded');
      } else {
        toast.error('Failed to generate PDF');
      }
    }
  };

  const handleCertificateSharePDF = async (format) => {
    if (certificateRef.current && printSubscription) {
      const filename = `membership-certificate-${printSubscription.subscription?.id?.slice(0,8)}.pdf`;
      const success = await shareReceiptPDF(certificateRef.current, filename, format);
      if (success) {
        toast.success('Certificate PDF shared');
      } else {
        toast.error('Failed to share PDF');
      }
    }
  };

  const getMemberName = (memberId) => {
    const member = members.find((m) => m.id === memberId);
    return member ? member.name : "Unknown";
  };

  const getPlanName = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    return plan ? plan.name : "Unknown";
  };

  const getPlanPrice = (planId) => {
    const plan = plans.find((p) => p.id === planId);
    return plan ? plan.price : 0;
  };

  const shouldShowRenew = (sub) => {
    if (sub.status !== 'active') return false;
    
    // Check if renewed (has a newer subscription)
    const hasNewer = subscriptions.some(s => 
      s.member_id === sub.member_id && 
      s.id !== sub.id && 
      new Date(s.start_date) > new Date(sub.start_date)
    );

    if (hasNewer) return false;

    const end = new Date(sub.end_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= (settings.membershipDueReminder || 7);
  };

  const handleRenew = (sub) => {
    const endDate = new Date(sub.end_date);
    const nextDay = new Date(endDate);
    nextDay.setDate(endDate.getDate() + 1);
    
    setFormData({
      member_id: sub.member_id,
      plan_id: sub.plan_id,
      start_date: nextDay.toISOString().split('T')[0],
    });
    setIsDialogOpen(true);
  };

  const getStatusBadge = (sub) => {
    const { end_date: endDate, status, member_id, id } = sub;

    if (status !== "active") {
      return (
        <Badge variant="secondary" className="flex items-center gap-1">
          <XCircle size={14} />
          Inactive
        </Badge>
      );
    }

    const end = new Date(endDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      // Check if renewed (has a newer subscription)
      const hasNewer = subscriptions.some(s => 
        s.member_id === member_id && 
        s.id !== id && 
        new Date(s.start_date) > new Date(sub.start_date)
      );

      if (hasNewer) {
        return (
          <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-300 flex items-center gap-1">
            <RotateCw size={14} />
            Expired - Renewed
          </Badge>
        );
      }

      return (
        <Badge variant="destructive" className="flex items-center gap-1">
          <XCircle size={14} />
          Expired
        </Badge>
      );
    } else if (daysUntilExpiry <= (settings.membershipDueReminder || 7)) {
      return (
        <Badge className="bg-orange-500 hover:bg-orange-600 flex items-center gap-1">
          <AlertCircle size={14} />
          Expiring Soon
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-lime-500 hover:bg-lime-600 text-zinc-900 flex items-center gap-1">
          <CheckCircle size={14} />
          Active
        </Badge>
      );
    }
  };

  const handlePayNow = (sub) => {
    setPaymentForm({
      receipt_type: "subscription_fee",
      member_id: sub.member_id,
      membership_id: sub.id,
      amount: (sub.amount_due || 0) - (sub.amount_paid || 0),
      payment_method: "cash",
      description: `Payment for ${getPlanName(sub.plan_id)}`,
      date: new Date().toISOString().split('T')[0]
    });
    setIsPaymentDialogOpen(true);
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      receipt_type: "subscription_fee",
      member_id: "",
      membership_id: "",
      amount: "",
      payment_method: "cash",
      description: "",
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...paymentForm,
        receipt_date: paymentForm.date
      };
      const response = await api.post('/receipts', payload);
      toast.success("Payment recorded successfully");
      
      // Prepare receipt for printing
      const member = members.find(m => m.id === paymentForm.member_id);
      const subscription = subscriptions.find(s => s.id === paymentForm.membership_id);
      
      const receiptData = {
        ...response.data,
        member_name: member?.name,
        member_email: member?.email,
        member_phone: member?.phone,
        plan_name: getPlanName(subscription?.plan_id),
        company_name: company?.name,
        company_address: company?.address,
        company_phone: company?.phone,
        company_email: company?.email,
        company_logo_url: company?.logo_url,
        amount_in_words: numberToWords(parseFloat(paymentForm.amount))
      };
      
      setLastCreatedReceipt(receiptData);
      setShowPrintOptions(true);
      setIsPaymentDialogOpen(false);
      resetPaymentForm();
      fetchData();
      // Notify other components
      try { window.dispatchEvent(new Event('ledgerUpdated')); } catch {}
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const handlePrintReceipt = (format) => {
    if (receiptRef.current) {
      printReceipt(receiptRef.current, format);
    }
  };
  
  const handleDownloadReceipt = async (format) => {
    if (receiptRef.current) {
      const success = await downloadReceiptPDF(
        receiptRef.current,
        `receipt-${lastCreatedReceipt?.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt downloaded successfully");
      } else {
        toast.error("Failed to download receipt");
      }
    }
  };
  
  const handleShareReceipt = async (format) => {
    if (receiptRef.current) {
      const success = await shareReceiptPDF(
        receiptRef.current,
        `receipt-${lastCreatedReceipt?.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt shared successfully");
      } else {
        toast.error("Failed to share receipt");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  const filteredSubscriptions = subscriptions.filter(sub => {
    const end = new Date(sub.end_date);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    
    // Status Filter
    let matchesStatus = true;
    if (statusFilter === 'active') {
       matchesStatus = sub.status === 'active' && daysUntilExpiry >= 0;
    } else if (statusFilter === 'expiring_soon') {
       matchesStatus = sub.status === 'active' && daysUntilExpiry >= 0 && daysUntilExpiry <= (settings.membershipDueReminder || 7);
    } else if (statusFilter === 'expired') {
       matchesStatus = sub.status === 'active' && daysUntilExpiry < 0; 
    } else if (statusFilter === 'inactive') {
       matchesStatus = sub.status !== 'active';
    }

    // Member Filter
    const matchesMember = memberFilter === 'all' || sub.member_id === memberFilter;

    // Plan Filter
    const matchesPlan = planFilter === 'all' || sub.plan_id === planFilter;

    // Payment Status Filter
    const matchesPayment = paymentStatusFilter === 'all' || sub.payment_status === paymentStatusFilter;

    return matchesStatus && matchesMember && matchesPlan && matchesPayment;
  }).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));

  return (
    <div data-testid="subscriptions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Subscriptions
          </h1>
          <p className="text-zinc-500">Manage member subscriptions and memberships</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-subscription-btn"
              className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
            >
              <Plus size={20} className="mr-2" />
              Assign Subscription
            </Button>
          </DialogTrigger>
          <DialogContent aria-describedby="subscription-dialog-description">
            <DialogHeader>
              <DialogTitle>Assign Subscription to Member</DialogTitle>
            </DialogHeader>
            <p id="subscription-dialog-description" className="sr-only">
              Assign a membership plan to a member
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="member">Select Member</Label>
                <Select
                  value={formData.member_id}
                  onValueChange={(value) => setFormData({ ...formData, member_id: value })}
                  required
                >
                  <SelectTrigger data-testid="subscription-member-select">
                    <SelectValue placeholder="Choose a member" />
                  </SelectTrigger>
                  <SelectContent>
                    {members.filter(m => m.status === 'active').map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name} - {member.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="plan">Select Plan</Label>
                <Select
                  value={formData.plan_id}
                  onValueChange={(value) => setFormData({ ...formData, plan_id: value })}
                  required
                >
                  <SelectTrigger data-testid="subscription-plan-select">
                    <SelectValue placeholder="Choose a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.filter(p => p.status === 'active').map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - {formatCurrency(plan.price)} ({plan.duration_days} days)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <input
                  type="date"
                  id="start_date"
                  data-testid="subscription-start-date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                  required
                />
              </div>
              <Button 
                type="submit" 
                data-testid="subscription-submit-btn"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
                disabled={!formData.member_id || !formData.plan_id}
              >
                Assign Subscription
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subscription</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="edit_plan">Plan</Label>
                <Select
                  value={editFormData.plan_id}
                  onValueChange={(value) => setEditFormData({ ...editFormData, plan_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.filter(p => p.status === 'active').map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - {formatCurrency(plan.price)} ({plan.duration_days} days)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit_start_date">Start Date</Label>
                <input
                  type="date"
                  id="edit_start_date"
                  value={editFormData.start_date}
                  onChange={(e) => setEditFormData({ ...editFormData, start_date: e.target.value })}
                  className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                />
              </div>
              <div>
                <Label htmlFor="edit_payment_status">Payment Status</Label>
                <Select
                  value={editFormData.payment_status}
                  onValueChange={(value) => setEditFormData({ ...editFormData, payment_status: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              >
                Update Subscription
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-zinc-200 mb-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="mb-2 block text-xs font-medium text-zinc-500 uppercase">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="expiring_soon">Expiring Soon</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="mb-2 block text-xs font-medium text-zinc-500 uppercase">Member</Label>
            <Select value={memberFilter} onValueChange={setMemberFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Members</SelectItem>
                {members.map(member => (
                  <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-2 block text-xs font-medium text-zinc-500 uppercase">Plan</Label>
            <Select value={planFilter} onValueChange={setPlanFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {plans.map(plan => (
                  <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-2 block text-xs font-medium text-zinc-500 uppercase">Payment</Label>
            <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by payment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Payments</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Subscriptions Table - Desktop */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
        <table className="min-w-max w-full">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">S.No</th>
              <th className="px-6 py-4 text-left">Member</th>
              <th className="px-6 py-4 text-left">Plan</th>
              <th className="px-6 py-4 text-left">Start Date</th>
              <th className="px-6 py-4 text-left">End Date</th>
              <th className="px-6 py-4 text-left">Price</th>
              <th className="px-6 py-4 text-left">Paid</th>
              <th className="px-6 py-4 text-left">Balance</th>
              <th className="px-6 py-4 text-left">Payment Status</th>
              <th className="px-6 py-4 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscriptions.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-6 py-12 text-center text-zinc-500">
                  No subscriptions found matching the selected filters.
                </td>
              </tr>
            ) : (
              filteredSubscriptions.map((sub, index) => (
                <Fragment key={sub.id}>
                <tr
                  data-testid={`subscription-row-${sub.id}`}
                  className={`hover:bg-zinc-100 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}`}
                >
                  <td className="px-6 py-4 font-medium text-zinc-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-zinc-400" />
                      <span className="font-medium text-zinc-900">{getMemberName(sub.member_id)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-zinc-700">{getPlanName(sub.plan_id)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Calendar size={14} />
                      {formatDate(sub.start_date)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-zinc-600">
                      <Calendar size={14} />
                      {formatDate(sub.end_date)}
                    </div>
                  </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-zinc-900 font-bold">
                    <span className="text-lime-500">{settings.currency}</span>
                    <span>{getPlanPrice(sub.plan_id)}</span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-green-700 font-medium">
                    {formatCurrency(sub.amount_paid || 0)}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-orange-700 font-medium">
                    {formatCurrency((sub.amount_due || 0) - (sub.amount_paid || 0))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {sub.payment_status === 'paid' ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle size={12} className="mr-1" />
                      Paid
                    </Badge>
                    ) : sub.payment_status === 'partial' ? (
                      <Badge className="bg-orange-100 text-orange-800">
                        <AlertCircle size={12} className="mr-1" />
                        Partial
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800">
                        <XCircle size={12} className="mr-1" />
                        Pending
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(sub)}
                  </td>
                </tr>
                {/* Actions Row */}
                <tr className={`${openHistory[sub.id] ? '' : 'border-b border-zinc-200 last:border-0'}`}>
                  <td className={`px-6 py-3 ${index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}`} colSpan="10">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleHistory(sub)}
                        className="p-2 hover:bg-zinc-50 rounded-md transition-colors"
                        title="Payment History"
                      >
                        <Receipt size={16} className="text-zinc-600" />
                      </button>
                      <button
                        onClick={() => handlePrintCertificate(sub)}
                        className="p-2 hover:bg-purple-50 rounded-md transition-colors"
                        title="Print Certificate"
                      >
                        <Printer size={16} className="text-purple-600" />
                      </button>
                      <button
                        onClick={() => handleEdit(sub)}
                        className="p-2 hover:bg-blue-50 rounded-md transition-colors"
                        title="Edit Subscription"
                      >
                        <Edit2 size={16} className="text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="p-2 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete Subscription"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                      {shouldShowRenew(sub) && (
                        <button
                          onClick={() => handleRenew(sub)}
                          className="p-2 hover:bg-orange-50 rounded-md transition-colors flex items-center gap-1 text-orange-600 font-medium text-sm"
                          title="Renew Subscription"
                        >
                          <RotateCw size={16} />
                          Renew
                        </button>
                      )}
                      {sub.payment_status !== 'paid' && (
                        <Button
                          onClick={() => handlePayNow(sub)}
                          className="bg-green-500 text-white hover:bg-green-600 text-sm px-3 py-1 h-8"
                        >
                          Pay Now
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {openHistory[sub.id] && (
                  <tr className="border-b border-zinc-200 last:border-0">
                    <td className={`px-6 py-4 ${index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'}`} colSpan="10">
                      {historyData[sub.id] && historyData[sub.id].length > 0 ? (
                        <div className="w-full">
                          <h4 className="text-sm font-semibold text-zinc-700 mb-2">Payment History</h4>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-zinc-500 bg-zinc-100/50 border-b border-zinc-200">
                                <th className="text-left px-4 py-2 font-medium">S.No</th>
                                <th className="text-left px-4 py-2 font-medium">Date</th>
                                <th className="text-left px-4 py-2 font-medium">Amount</th>
                                <th className="text-left px-4 py-2 font-medium">Type</th>
                                <th className="text-left px-4 py-2 font-medium">Method</th>
                                <th className="text-left px-4 py-2 font-medium">Description</th>
                                <th className="text-center px-4 py-2 font-medium">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyData[sub.id].map((r, idx) => (
                                <tr key={r.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50">
                                  <td className="px-4 py-2 text-zinc-500">{idx + 1}</td>
                                  <td className="px-4 py-2">{formatDate(r.receipt_date)}</td>
                                  <td className="px-4 py-2 text-green-700 font-medium">{formatCurrency(r.amount)}</td>
                                  <td className="px-4 py-2 capitalize">{(r.receipt_type || '').replace('_', ' ')}</td>
                                  <td className="px-4 py-2 capitalize">{(r.payment_method || '').replace('_', ' ')}</td>
                                  <td className="px-4 py-2 text-zinc-600">{r.description || '-'}</td>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center justify-center gap-2">
                                      <button onClick={() => openReceiptEdit(r)} className="p-1 hover:bg-blue-50 rounded text-blue-600 transition-colors" title="Edit">
                                        <Edit2 size={14} />
                                      </button>
                                      <button onClick={() => deleteReceipt(r)} className="p-1 hover:bg-red-50 rounded text-red-600 transition-colors" title="Delete">
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-4 text-zinc-500 italic">No payment history found</div>
                      )}
                    </td>
                  </tr>
                )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Subscriptions Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {subscriptions.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500">
            No subscriptions found. Assign memberships to members to get started.
          </div>
        ) : (
          subscriptions.map((sub, index) => (
            <div
              key={sub.id}
              data-testid={`subscription-card-${sub.id}`}
              className={`${index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} border border-zinc-200 rounded-lg p-4 shadow-sm`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User size={14} className="text-zinc-400" />
                    <h3 className="font-bold text-zinc-900">{getMemberName(sub.member_id)}</h3>
                  </div>
                  <div className="text-sm text-zinc-600 font-medium">{getPlanName(sub.plan_id)}</div>
                </div>
                {getStatusBadge(sub)}
              </div>

              {/* Dates */}
              <div className="space-y-2 mb-3 pb-3 border-b border-zinc-100">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Start Date:</span>
                  <span className="text-zinc-900 font-medium">{formatDate(sub.start_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">End Date:</span>
                  <span className="text-zinc-900 font-medium">{formatDate(sub.end_date)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div className="bg-zinc-50 rounded-lg p-3 mb-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-zinc-500">Price:</span>
                  <span className="text-lg font-bold text-zinc-900">{formatCurrency(getPlanPrice(sub.plan_id))}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-zinc-500">Paid:</span>
                  <span className="text-sm font-bold text-green-700">{formatCurrency(sub.amount_paid || 0)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs text-zinc-500">Balance:</span>
                  <span className="text-sm font-bold text-orange-700">{formatCurrency((sub.amount_due || 0) - (sub.amount_paid || 0))}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-zinc-500">Payment Status:</span>
                  {sub.payment_status === 'paid' ? (
                    <Badge className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-1">
                      <CheckCircle size={12} />
                      Paid
                    </Badge>
                  ) : sub.payment_status === 'partial' ? (
                    <Badge className="bg-orange-500 hover:bg-orange-600 flex items-center gap-1">
                      <AlertCircle size={12} />
                      Partial
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <XCircle size={12} />
                      Pending
                    </Badge>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {shouldShowRenew(sub) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRenew(sub)}
                    className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                  >
                    <RotateCw size={14} className="mr-1" />
                    Renew
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePrintCertificate(sub)}
                  className="flex-1"
                >
                  <Printer size={14} className="mr-1" />
                  Print
                </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(sub)}
                className="flex-1"
              >
                <Edit2 size={14} className="mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDelete(sub.id)}
                className="px-3 text-red-600"
              >
                <Trash2 size={14} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => toggleHistory(sub)}
                className="flex-1"
              >
                <Receipt size={14} className="mr-1" />
                History
              </Button>
            </div>
            {openHistory[sub.id] && (
              <div className={`mt-3 ${index % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} rounded-lg p-3`}>
                {historyData[sub.id] && historyData[sub.id].length > 0 ? (
                  <div className="space-y-2">
                    {historyData[sub.id].map((r) => (
                      <div key={r.id} className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center border-b pb-2">
                        <span className="text-zinc-500">{formatDate(r.receipt_date)}</span>
                        <span className="text-green-700 font-medium">{formatCurrency(r.amount)}</span>
                        <span className="capitalize">{(r.receipt_type || '').replace('_', ' ')}</span>
                        <span className="capitalize">{(r.payment_method || '').replace('_', ' ')}</span>
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => openReceiptEdit(r)} className="text-blue-600 border-blue-200">Edit</Button>
                          <Button size="sm" variant="outline" onClick={() => deleteReceipt(r)} className="text-red-600 border-red-200">Delete</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-zinc-500 text-sm">No payment history</div>
                )}
              </div>
            )}
            {sub.payment_status !== 'paid' && (
              <Button
                onClick={() => handlePayNow(sub)}
                className="w-full bg-green-500 text-white hover:bg-green-600 mt-2"
              >
                <DollarSign size={14} className="mr-1" />
                Pay Now
              </Button>
            )}
            </div>
          ))
        )}
      </div>

      {showCertificatePrintDialog && printSubscription && (
        <Dialog open={showCertificatePrintDialog} onOpenChange={setShowCertificatePrintDialog}>
          <DialogContent aria-describedby="certificate-print-dialog">
            <DialogHeader>
              <DialogTitle>Print Membership Certificate</DialogTitle>
            </DialogHeader>
            <p id="certificate-print-dialog" className="text-sm text-zinc-600 mb-4">
              {printSubscription.member?.name} - {printSubscription.plan?.name}
            </p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => handleCertificatePrint('a4')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A4
                </Button>
                <Button onClick={() => handleCertificatePrint('a5')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A5
                </Button>
              </div>
              <Button onClick={() => handleCertificatePrint('thermal')} className="w-full bg-purple-500 hover:bg-purple-600">
                <Printer size={16} className="mr-2" />
                Print Thermal
              </Button>
            </div>
            <div className="border-t pt-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => handleCertificateDownloadPDF('a4')} variant="outline">
                  Download PDF
                </Button>
                <Button onClick={() => handleCertificateSharePDF('a4')} variant="outline">
                  Share PDF
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden Certificate for Printing */}
      {printSubscription && (
        <SubscriptionCertificate
          ref={certificateRef}
          subscription={printSubscription.subscription}
          member={printSubscription.member}
          plan={printSubscription.plan}
          company={printSubscription.company}
        />
      )}

      <Dialog open={isReceiptEditOpen} onOpenChange={setIsReceiptEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitReceiptEdit} className="space-y-4">
            <div>
              <Label htmlFor="edit_amount">Amount</Label>
              <input
                id="edit_amount"
                type="number"
                step="0.01"
                value={receiptEditForm.amount}
                onChange={(e) => setReceiptEditForm({ ...receiptEditForm, amount: e.target.value })}
                className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_payment_method">Payment Method</Label>
              <Select
                value={receiptEditForm.payment_method}
                onValueChange={(value) => setReceiptEditForm({ ...receiptEditForm, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="edit_description">Description</Label>
              <input
                id="edit_description"
                value={receiptEditForm.description}
                onChange={(e) => setReceiptEditForm({ ...receiptEditForm, description: e.target.value })}
                className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
              />
            </div>
            <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">Update Payment</Button>
          </form>
        </DialogContent>
      </Dialog>
      {/* Payment Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePaymentSubmit} className="space-y-4">
            <div>
              <Label htmlFor="payment_amount">Amount</Label>
              <Input
                id="payment_amount"
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payment_date">Date</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentForm.date}
                onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="payment_method">Payment Method</Label>
              <Select
                value={paymentForm.payment_method}
                onValueChange={(value) => setPaymentForm({ ...paymentForm, payment_method: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="credit_card">Credit Card</SelectItem>
                  <SelectItem value="debit_card">Debit Card</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payment_description">Description</Label>
              <Input
                id="payment_description"
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
              />
            </div>
            <Button type="submit" className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold">
              Record Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Options Dialog */}
      <Dialog open={showPrintOptions} onOpenChange={setShowPrintOptions}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receipt Created</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Button onClick={() => handlePrintReceipt('thermal')} variant="outline" className="h-24 flex flex-col gap-2">
              <Printer size={24} />
              <span>Thermal Print</span>
            </Button>
            <Button onClick={() => handlePrintReceipt('a4')} variant="outline" className="h-24 flex flex-col gap-2">
              <FileText size={24} />
              <span>A4 Print</span>
            </Button>
            <Button onClick={() => handlePrintReceipt('a5')} variant="outline" className="h-24 flex flex-col gap-2">
              <FileText size={24} />
              <span>A5 Print</span>
            </Button>
            <Button onClick={() => handleDownloadReceipt('a4')} variant="outline" className="h-24 flex flex-col gap-2">
              <Download size={24} />
              <span>Download PDF</span>
            </Button>
            <Button onClick={() => handleShareReceipt('a4')} variant="outline" className="h-24 flex flex-col gap-2">
              <Share2 size={24} />
              <span>Share PDF</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Hidden Receipt Component */}
      <div style={{ display: 'none' }}>
        <ReceiptPrint ref={receiptRef} data={lastCreatedReceipt} settings={settings} />
      </div>

    </div>
  );
};

export default Subscriptions;
